import { OperationVariables, QueryResult, TypedDocumentNode } from '@apollo/client';
import { useHistory } from 'react-router-dom';

import {
  GetEventDemographicsStatsQuery as GetEventDemographicsStats,
  GetEventFundraisingStatsQuery as GetEventFundraisingStats,
  GetEventRegistrationsStatsQuery as GetEventRegistrationsStats,
} from '__generated__/graphql';
import { gql } from '__generated__';

import { isGraphQLValidationError } from '../../common/helpers';
import useDashboardUrlState from './useDashboardUrlState';
import useProject from '../useProject';
import useQuery from '../../api/useQuery';

export const DashboardEventFieldsFragment = gql(`
  fragment DashboardEventFields on Event {
    id
    registrations_count(tickets: $tickets)
  }
`);

export const GetEventRegistrationsStatsQuery = gql(`
  query GetEventRegistrationsStats($eventId: ID!, $since: String!, $tickets: [ID!], $registrationCountFilters: [AnalyticsFilters!]!) {
    event(id: $eventId) {
      ...DashboardEventFields
      teams_count(tickets: $tickets)
      orders_count(tickets: $tickets)
      revenue(tickets: $tickets)
      new_registrations_count: registrations_count(tickets: $tickets, since: $since)
      new_teams_count: teams_count(tickets: $tickets, since: $since)
      new_orders_count: orders_count(tickets: $tickets, since: $since)
      new_revenue: revenue(tickets: $tickets, since: $since)
      sessions_count
      queue_size
      tickets {
        id
        title
        sold_count
        revenue
        remaining_amount
        active_purchases_count
        accepted_purchases_amount
        invitation_reservations_count
        capacity
        indirect_capacity
        active
        position
        promotions {
          id
          title
          sold_count
          revenue
          remaining_amount
          active_purchases_count
          accepted_purchases_amount
          invitation_reservations_count
          capacity
          start_date
          end_date
          active
        }
        time_slots {
          id
          start_date
          start_time
          title
          sold_count
          revenue
          remaining_amount
          active_purchases_count
          accepted_purchases_amount
          invitation_reservations_count
          capacity
          multi_date
        }
      }
      products {
        id
        title
        sold_count(tickets: $tickets)
        revenue(tickets: $tickets)
        remaining_amount(tickets: $tickets)
        capacity
        indirect_capacity
        active
        promotions {
          id
          restricted_to_tickets {
            id
          }
        }
        product_variants {
          id
        }
      }
      participant_fields {
        id
        title
        enabled
        field_entry_count(tickets: $tickets)
        choices {
          id
        }
        tickets {
          id
        }
        products {
          id
        }
        promotions {
          id
        }
      }
    }
    registrationCounts(filters: $registrationCountFilters) {
      event {
        id
        title
        start_date
        tickets {
          id
          title
        }
      }
      days
      counts
    }
  }
`);

export const useEventRegistrationsQuery = (eventId: string, startDate: string) => {
  const [urlState] = useDashboardUrlState();

  return useEventDashboardQuery(GetEventRegistrationsStatsQuery, eventId, {
    since: startDate,
    registrationCountFilters: [
      {
        event: eventId,
        tickets: urlState.tickets,
      },
      ...urlState.comparedEvents.map((param) => ({
        event: param.event,
        tickets: param.tickets,
      })),
    ],
  });
};

export const GetEventDemographicsStatsQuery = gql(`
  query GetEventDemographicsStats($eventId: ID!, $tickets: [ID!]) {
    event(id: $eventId) {
      ...DashboardEventFields
      analytics(tickets: $tickets) {
        repeaters {
          count
          events {
            event {
              id
              title
              start_date
            }
            count
          }
        }
        age_groups {
          average_age
          groups {
            min_age
            max_age
            male
            female
            neutral
            total
          }
        }
        countries {
          country
          count
        }
        states {
          country
          state
          count
        }
        cities {
          country
          state
          city
          count
        }
        tracking {
          initial_url {
            value
            count
          }
          final_url {
            value
            count
          }
          utm_source {
            value
            count
          }
          utm_medium {
            value
            count
          }
          utm_campaign {
            value
            count
          }
        }
      }
    }
  }
`);

export const useEventDemographicsQuery = (eventId: string) => (
  useEventDashboardQuery(GetEventDemographicsStatsQuery, eventId, {})
);

export const GetEventFundraisingStatsQuery = gql(`
  query GetEventFundraisingStats($eventId: ID!, $tickets: [ID!]) {
    event(id: $eventId) {
      ...DashboardEventFields
      analytics(tickets: $tickets) {
        fundraising {
          charity {
            id
            title
            logo_url
            description
          }
          campaigns_count
          funds_raised
        }
      }
    }
  }
`);

export const useEventFundraisingQuery = (eventId: string) => (
  useEventDashboardQuery(GetEventFundraisingStatsQuery, eventId, {})
);

type Data =
  GetEventRegistrationsStats
  | GetEventDemographicsStats
  | GetEventFundraisingStats;

function useEventDashboardQuery<TData extends Data, TVariables extends OperationVariables>(
  query: TypedDocumentNode<TData, TVariables>,
  eventId: string,
  variables: Omit<TVariables, 'eventId' | 'tickets'>,
): {
    event: QueryResult<TData, TVariables>['data']['event'] | null,
    data: QueryResult<TData, TVariables>['data'],
    loading: boolean,
  } {
  const project = useProject();
  const history = useHistory();
  const [urlState] = useDashboardUrlState();

  const { data, loading } = useQuery<TData>(
    query,
    {
      variables: {
        ...variables,
        eventId,
        tickets: urlState.tickets,
      },
      onError: (error) => {
        if (isGraphQLValidationError(error)) {
          // Event not found
          history.push(`/${project.id}/dashboard`);
        }
      },
      pollInterval: 30000,
    },
  );

  const event = data?.event.id === eventId ? data?.event : null;

  return { event, data, loading };
}
